import {
  useCalculateOrderTotal,
  useGetTicketTypesByEventId,
} from '@sunrise-ui/api-client';
import type { CalculateTotalOutput, Promo } from '@sunrise-ui/api/events';
import { useCheckoutModalStore, useDebounce } from '@sunrise-ui/primitives';
import { useMemo } from 'react';

type UseOrderCalculationsParams = {
  ticketQuantities: Map<string, number>;
  appliedPromo?: Promo;
};

type OrderCalculations = {
  data?: Partial<CalculateTotalOutput>;
  hasEligibleTickets: boolean;
  isCalculating: boolean;
  isError: boolean;
  refetch: () => void;
};

/**
 * Centralized hook for order calculations including subtotal, discounts, final total, and line items.
 * - Gets eventId from checkout modal store
 * - Fetches ticket types for the event
 * - Calculates local data instantly (without taxes) for immediate UI feedback
 * - Fetches API calculation (with taxes) with debouncing
 * - Returns API data if available, otherwise returns local data
 */
export function useOrderCalculations({
  ticketQuantities,
  appliedPromo,
}: UseOrderCalculationsParams): OrderCalculations {
  const { eventId } = useCheckoutModalStore();

  const { data: ticketTypes } = useGetTicketTypesByEventId({ eventId });

  const inputValue = useMemo(
    () => ({ ticketQuantities, appliedPromo }),
    [ticketQuantities, appliedPromo],
  );

  // Debounced values for API call
  const {
    ticketQuantities: debouncedTicketQuantities,
    appliedPromo: debouncedPromo,
  } = useDebounce(inputValue, 500);

  // Query for API calculation with taxes (shared across components)
  const {
    data: apiData,
    isPending,
    isError,
    refetch,
  } = useCalculateOrderTotal(debouncedTicketQuantities, debouncedPromo);

  // Calculate local data (instant, no taxes)
  const { hasEligibleTickets, data: localData } = useMemo(() => {
    if (!ticketTypes) {
      return {
        hasEligibleTickets: false,
      };
    }

    // Build line items
    const line_items = ticketTypes
      .map((type) => ({
        ticket_type_id: type.id,
        ticket_type_name: type.name,
        unit_price: type.price,
        quantity: ticketQuantities.get(type.id) || 0,
        line_total: type.price * (ticketQuantities.get(type.id) || 0),
      }))
      .filter((item) => item.quantity > 0);

    // Calculate subtotal
    const subtotal = ticketTypes.reduce((sum, type) => {
      const quantity = ticketQuantities.get(type.id) || 0;
      return sum + type.price * quantity;
    }, 0);

    // Calculate discount amount and check eligibility
    let discountAmount = 0;
    let hasEligibleTickets = false;

    if (appliedPromo) {
      const eligibleTicketTypeIds = new Set(
        appliedPromo.ticket_types?.map((ticket) => ticket.id) || [],
      );

      // Check if any selected tickets are eligible for the discount
      const selectedTicketIds = Array.from(ticketQuantities.entries())
        .filter(([, quantity]) => quantity > 0)
        .map(([ticketId]) => ticketId);

      hasEligibleTickets = selectedTicketIds.some((id) =>
        eligibleTicketTypeIds.has(id),
      );

      // Calculate subtotal only for eligible ticket types
      const eligibleSubtotal = ticketTypes.reduce((total, type) => {
        if (eligibleTicketTypeIds.has(type.id)) {
          const quantity = ticketQuantities.get(type.id) || 0;
          return total + type.price * quantity;
        }
        return total;
      }, 0);

      if (appliedPromo.discount_type === 'percent') {
        discountAmount = eligibleSubtotal * (appliedPromo.discount / 100);
      } else {
        // Fixed amount discount
        discountAmount = Math.min(eligibleSubtotal, appliedPromo.discount);
      }
    }

    const finalTotal = subtotal - discountAmount;

    return {
      data: {
        line_items,
        subtotal,
        total: finalTotal,
        promo_code_applied: appliedPromo?.discount_code,
      },
      hasEligibleTickets,
    };
  }, [ticketTypes, ticketQuantities, appliedPromo]);

  // Check if any tickets are selected
  const hasTickets = Array.from(ticketQuantities.values()).some(
    (qty) => qty > 0,
  );

  // Use API data if available, otherwise use local data
  let data: Partial<CalculateTotalOutput> | undefined;
  let isCalculating = isPending && hasTickets;
  if (
    debouncedTicketQuantities !== ticketQuantities ||
    debouncedPromo !== appliedPromo
  ) {
    data = localData;
    isCalculating = true;
  } else {
    // TODO: This is just temporary while the BE is being fixed
    if (apiData) {
      data = {
        ...apiData,
        line_items: localData?.line_items,
      };
    } else {
      data = localData;
    }
  }

  return {
    data,
    hasEligibleTickets,
    isCalculating,
    isError,
    refetch,
  };
}
